import { Component } from 'react';
import './App.css';
import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useReducer,
  useState,
  useSyncExternalStore,
} from 'react';
import { Routes, Route, Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { createGlobalStyle, css } from 'styled-components';
import { modalManager } from 'utils';
import { networkResponseLisenner } from 'utils/api';
import { Store, NavBar, Login, Modal, Fotter, Loader, Checkout, Invoice } from 'components';
import { Error } from 'status';
import { AuthGuard, AuthProvider } from 'Auth';
import run from 'run';
import background from 'assets/background.jpg';

const GlobalStyle = createGlobalStyle`
    ${({ isLogin }) =>
      isLogin &&
      css`
        html {
          background: url(${background}) no-repeat fixed;
          background-size: cover;
          @media (min-width: 1025px) {
            background-position: center;
          }
        }
      `}`;

const appState = {
  isError: false,
  isInited: false,
  isLoading: false,
  isMenuOpen: false,
  modal: {
    type: 'info',
    header: '',
    component: null,
    data: {},
    isOpen: false,
    allowClose: false,
  },
  user: null,
  store: null,
  storeEvent: 1,
  storeType: 'E',
  selectedStorePackage: null,
  status: '',
  dailyBonusTime: null,
};

export const appActions = {
  setError: 'setError',
  setStoreType: 'setStoreType',
  setStatus: 'setStatus',
  multiUpdate: 'multiUpdate',
  selectStorePackage: 'selectStorePackage',
  setUserData: 'setUserData',
  setModal: 'setModal',
  setDailyBonusTime: 'setDailyBonusTime',
};

const reducer = (state, action) => {
  switch (action.type) {
    case appActions.setError:
      return { ...state, isError: action.value };

    case appActions.setStoreType:
      return { ...state, storeType: action.value, isError: false };

    case appActions.setStatus:
      return { ...state, setStatus: action.value };

    case appActions.multiUpdate:
      return { ...state, ...action.value };

    case appActions.selectStorePackage:
      return { ...state, selectedStorePackage: action.value, store: null, storeType: '-1' };

    case appActions.setUserData:
      return { ...state, user: action.value };

    case appActions.setModal:
      return { ...state, modal: { ...state.modal, ...action.value } };

    case appActions.setDailyBonusTime:
      return { ...state, dailyBonusTime: action.value };

    default:
      throw new Error();
  }
};

const AppStateContext = createContext();
AppStateContext.displayName = 'AppStateContext';

export const useApp = () => {
  const appState = useContext(AppStateContext);
  return appState;
};

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, appState);
  const navigate = useNavigate();

  const networkResponseList = useSyncExternalStore(
    networkResponseLisenner.addListener,
    networkResponseLisenner.get,
  );

  const handleNetworkStatus = useCallback(async response => {
    const { status } = response;
    switch (status) {
      case 400:
        navigate('/', { replace: true });
        break;
      case 401: {
        const data = await response.json();
        if (data) {
          const key = Object.keys(data)[0];
          const { eNr } = data[key];
          switch (eNr) {
            case 801:
              openModal({ modal: 'loginSessionExpired' });
              break;
            case 803:
              openModal({ modal: 'banned' });
              break;
          }
        }
        break;
      }
      case 404:
        openModal({ modal: 'userNotFound' });
        break;
    }
  }, []);

  useEffect(() => {
    const numItems = networkResponseList.length;
    if (numItems) {
      handleNetworkStatus(networkResponseList[numItems - 1]);
    }
  }, [networkResponseList, handleNetworkStatus]);

  const update = (type = '', value = null) => dispatch({ type, value });

  useEffect(() => {
    run({ eId: state.storeEvent, hashParams: window.location.hash }).then(res =>
      update(appActions.multiUpdate, { ...res, isInited: true }),
    );
  }, []);

  const setGlobalError = useCallback(sts => update(appActions.setError, sts), []);
  const setStoreType = useCallback(storeType => update(appActions.setStoreType, storeType), []);
  const setStoreData = useCallback(data => update(appActions.multiUpdate, data), []);
  const selectStorePackage = useCallback(data => update(appActions.selectStorePackage, data), []);
  const setUserData = useCallback(data => update(appActions.setUserData, data), []);
  const resetAppState = useCallback(
    () => update(appActions.multiUpdate, { ...appState, isInited: true }),
    [],
  );
  const openModal = useCallback(({ modal, options, data }) => {
    const modalProps = modalManager[modal];
    update(appActions.setModal, { ...modalProps, ...options, data, isOpen: true });
  }, []);
  const closeModal = useCallback(() => update(appActions.setModal, appState.modal), []);
  const setDailyBonusTime = useCallback(timestamp => update(appActions.setDailyBonusTime, timestamp), []);

  let app = {
    state,
    setStoreType,
    setStoreData,
    selectStorePackage,
    setUserData,
    resetAppState,
    setGlobalError,
    openModal,
    closeModal,
    setDailyBonusTime,
  };

  return (
    <AppStateContext.Provider value={app}>
      {state.isInited ? children : <Loader fullHeight />}
    </AppStateContext.Provider>
  );
};

const Main = () => {
  const { state } = useApp();
  const { isError } = state;

  return (
    <AuthGuard>
      <NavBar />
      {isError ? <Error /> : <Outlet />}
    </AuthGuard>
  );
};

const NoMatch = () => {
  return (
    <div className="container" style={{ textAlign: 'center' }}>
      <h1>404</h1>
      <p>
        Oppss.. there is nothing here, you may want to back <Link to="/">home</Link>.
      </p>
    </div>
  );
};

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    return this.state.isError ? <Error isStoreButton={false} /> : this.props.children;
  }
}

const App = () => {
  const isIos = /iPhone|iPad|iPod|Mac/i.test(navigator.userAgent);
  const [isInstalled, setIsInstalled] = useState(
    isIos && !window.matchMedia('(display-mode: standalone)').matches ? false : true,
  );
  const [installPrompt, setInstallPrompt] = useState(null);
  const { pathname } = useLocation();

  const handleBeforeInstallPrompt = event => {
    event.preventDefault();
    setInstallPrompt(event);
    setIsInstalled(false);
  };

  const handlePwaButton = () => {
    if (installPrompt) {
      installPrompt.prompt();
      installPrompt.userChoice.then(choiceResult => {
        choiceResult.outcome === 'accepted' && setIsInstalled(true);
        setInstallPrompt(null);
      });
    }
  };

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  return (
    <ErrorBoundary>
      <AppProvider>
        <GlobalStyle isLogin={pathname === '/login'} />
        <AuthProvider>
          <Modal />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<NoMatch />} />
            <Route element={<Main />}>
              <Route
                path="/"
                element={<Store isInstalled={isInstalled} handlePwaButton={handlePwaButton} />}
              />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/invoice_status/:iId" element={<Invoice />} />
            </Route>
          </Routes>
          <Fotter />
        </AuthProvider>
      </AppProvider>
    </ErrorBoundary>
  );
};

export default App;
